import { DataSourceType } from '@/components/DataMapEditor/hooks/useInitDataMap';
import { DataSourceTableType } from '@/types/dataMap';
import { apiClient } from '@packages/api-client/src';
import { message } from 'antd';

export enum DatasourceType {
  FILES = 'FILES',
  RELATIONSHIP = 'RELATIONSHIP',
}

// 通用API响应类型约束
export interface DataMapApiResponse<T = unknown> {
  message: string;
  code: number;
  data: T;
}

// 基础响应类型（无data字段）
export interface DataMapApiBaseResponse {
  message: string;
  code: number;
}

// 场景1：databaseType = 'files' 时的参数类型（仅file可选，其他必空）
interface FileDatasourceParams {
  name: string; // 强制传空字符串
  datasourceType: DatasourceType;
}

// 场景2：databaseType ≠ 'files' 时的参数类型（除file外都必传）
interface NormalDatasourceParams {
  name: string;
  datasourceType: DatasourceType;
  databaseType: string; // 排除'files'
  host: string;
  port: string;
  username: string;
  password: string;
  initDatabase: string;
}

// 联合类型：二选一
type CreateRelateDataSourceParams =
  | FileDatasourceParams
  | NormalDatasourceParams;

interface ConditionsParamsType {
  leftColumnId: number | null;
  rightColumnId: number | null;
  operator: string;
}

export interface mapViewsAddTableJoinsParmas {
  leftTableId: number | null;
  rightTableId: number | null;
  joinType: string;
  conditions: ConditionsParamsType[];
}

export interface OntologyStructureAttribute {
  id: number;
  name: string;
  description: string;
  fromObjectName: string;
}

export interface OntologyStructureObject {
  id: number;
  name: string;
  description: string;
  attributes: OntologyStructureAttribute[];
}

// DataMap API 类
export class DataMapApi {
  // 上传本地数据文件
  static async uploadDataSources(
    files: File,
    dataSourcesId: string,
    tableName?: string
  ): Promise<DataMapApiBaseResponse> {
    try {
      const formData = new FormData();
      formData.append('file', files);
      if (tableName) {
        formData.append('tableName', tableName);
      }
      const response = await apiClient
        .getInstance()
        .post(`/datasources/${dataSourcesId}/upload`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          timeout: 300000, // 5分钟超时
        });

      if (response.data.code !== 200) {
        throw new Error(response.data.message || '上传本地数据文件失败');
      }
      return {
        message: response.data.message,
        code: response.data.code,
      };
    } catch (error) {
      console.error('❌ 上传本地数据文件失败:', error);
      throw error;
    }
  }
  // 创建关联数据源
  static async createRelateDataSource(
    params: CreateRelateDataSourceParams
  ): Promise<DataMapApiResponse<DataSourceType>> {
    try {
      const requestParams = Object.fromEntries(
        Object.entries(params).filter(([_, value]) => value !== undefined)
      );

      const response = await apiClient
        .getInstance()
        .post('/datasources', requestParams, {
          headers: {
            'Content-Type': 'application/json',
          },
          timeout: 300000, // 5分钟超时
        });

      if (response.data.code !== 200) {
        throw new Error(response.data.message || '创建关联数据源失败');
      }
      const data = response.data.data;
      return {
        message: response.data.message,
        code: response.data.code,
        data: {
          id: data.id,
          name: data.name,
          datasourceType: data.datasourceType,
          databaseType: data.databaseType,
          host: data.host,
          port: data.port,
          username: data.username,
          initDatabase: data.initDatabase,
          tables: data?.tables || [],
        },
      };
    } catch (error) {
      console.error('❌ 创建关联数据源失败:', error);
      throw error;
    }
  }
   // 更新关联数据源
  static async updataRelateDataSource(
    params: CreateRelateDataSourceParams
  ): Promise<DataMapApiResponse<DataSourceType>> {
    try {
      const requestParams = Object.fromEntries(
        Object.entries(params).filter(([_, value]) => value !== undefined)
      );

      const response = await apiClient
        .getInstance()
        .put('/datasources', requestParams, {
          headers: {
            'Content-Type': 'application/json',
          },
          timeout: 300000, // 5分钟超时
        });

      if (response.data.code !== 200) {
        throw new Error(response.data.message || '创建关联数据源失败');
      }
      const data = response.data.data;
      return {
        message: response.data.message,
        code: response.data.code,
        data: {
          id: data.id,
          name: data.name,
          datasourceType: data.datasourceType,
          databaseType: data.databaseType,
          host: data.host,
          port: data.port,
          username: data.username,
          initDatabase: data.initDatabase,
          tables: data?.tables || [],
        },
      };
    } catch (error) {
      console.error('❌ 创建关联数据源失败:', error);
      throw error;
    }
  }
  // 获取数据源列表
  static async getDataSourcesList(): Promise<
    DataMapApiResponse<DataSourceType[]>
  > {
    try {
      const response = await apiClient.getInstance().get('/datasources', {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 300000, // 5分钟超时
      });

      if (response.data.code !== 200) {
        throw new Error(response.data.message || '获取数据源列表失败');
      }
      return {
        message: response.data.message,
        code: response.data.code,
        data: response.data.data,
      };
    } catch (error) {
      console.error('❌ 获取数据源列表失败:', error);
      throw error;
    }
  }
  // 删除数据源
  static async deleteDataSource(
    dataSourceId: string
  ): Promise<DataMapApiBaseResponse> {
    try {
      const response = await apiClient
        .getInstance()
        .delete(`/datasources/${dataSourceId}`, {
          headers: {
            'Content-Type': 'application/json',
          },
          timeout: 300000, // 5分钟超时
        });
      if (response.data.code !== 200) {
        throw new Error(response.data.message || '删除数据源失败');
      }
      const data = response.data;
      return {
        message: data.message,
        code: data.code,
      };
    } catch (error) {
      console.error('❌ 删除数据源失败:', error);
      throw error;
    }
  }
  // 保存表信息到数据源
  static async saveTableDataSource(
    dataSourceId: string,
    tableName: string
  ): Promise<DataMapApiBaseResponse> {
    try {
      const response = await apiClient
        .getInstance()
        .post(`/datasources/${dataSourceId}/metadata/${tableName}`, undefined, {
          headers: {
            'Content-Type': 'application/json',
          },
          timeout: 300000, // 5分钟超时
        });
      if (response.data.code !== 200) {
        throw new Error(response.data.message || '保存数据表到数据源失败');
      }
      const data = response.data;
      return {
        message: data.message,
        code: data.code,
      };
    } catch (error) {
      console.error('❌ 保存数据表到数据源失败:', error);
      throw error;
    }
  }
  // 根据表id获取表详细信息
  static async getTableDetail(
    dataSourceId: string,
    tableId: string
  ): Promise<DataMapApiResponse<DataSourceTableType>> {
    try {
      const response = await apiClient
        .getInstance()
        .get(`/datasources/${dataSourceId}/saved-metadata/${tableId}`, {
          headers: {
            'Content-Type': 'application/json',
          },
          timeout: 300000, // 5分钟超时
        });
      if (response.data.code !== 200) {
        throw new Error(response.data.message || '获取表详细信息失败');
      }
      return {
        message: response.data.message,
        code: response.data.code,
        data: response.data.data,
      };
    } catch (error) {
      console.error('❌ 获取表详细信息失败:', error);
      throw error;
    }
  }
  // 获取数据源元信息--获取所有数据表，用于添加到数据源
  static async getDataSourceMetadata(
    dataSourceId: string
  ): Promise<DataMapApiResponse<DataSourceTableType[]>> {
    try {
      const response = await apiClient
        .getInstance()
        .get(`/datasources/${dataSourceId}/metadata`, {
          headers: {
            'Content-Type': 'application/json',
          },
          timeout: 300000, // 5分钟超时
        });
      if (response.data.code !== 200) {
        throw new Error(response.data.message || '获取数据源元信息');
      }
      return {
        message: response.data.message,
        code: response.data.code,
        data: response.data.data,
      };
    } catch (error) {
      console.error('❌ 获取数据源元信息失败:', error);
      throw error;
    }
  }
  // 创建映射视图
  static async creatObjectMappingsViews(
    ontologyObjectId: string
  ): Promise<DataMapApiResponse<number>> {
    try {
      const response = await apiClient.getInstance().post(
        '/mapping-views',
        {
          ontologyObjectId,
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
          timeout: 300000, // 5分钟超时
        }
      );

      if (response.data.code !== 200) {
        throw new Error(response.data.message || '创建映射视图失败');
      }
      return {
        message: response.data.message,
        code: response.data.code,
        data: response.data.data,
      };
    } catch (error) {
      console.error('❌ 创建映射视图失败:', error);
      throw error;
    }
  }
  // 映射视图添加数据表
  static async mappingsAddTable(
    tableMetadataId: string,
    mappingViewId: string
  ): Promise<DataMapApiResponse<number>> {
    try {
      const response = await apiClient.getInstance().post(
        `/mapping-views/${mappingViewId}/sources`,
        {
          tableMetadataId,
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
          timeout: 300000, // 5分钟超时
        }
      );
      // ！todo 特殊情况，重复数据会返回状态码400， 后台暂时无法修改状态码，手动兼容下
      if (response.data.code !== 200 && response.data.code !== 400) {
        throw new Error(response.data.message || '映射视图添加数据表失败');
      }

      return {
        message: response.data.message,
        code: response.data.code,
        data: response.data?.data || {},
      };
    } catch (error: any) {
      if (error.message) message.error(error.message);
      console.error('❌映射视图添加数据表失败:', error);
      throw error;
    }
  }
  // 映射视图添加字段映射
  static async mappingsViewsAddField(
    mappingViewId: string,
    ontologyAttributeId: string,
    columnMetadataId: string,
    viewSourceId: string
  ): Promise<DataMapApiResponse<number>> {
    try {
      const response = await apiClient.getInstance().post(
        `/mapping-views/${mappingViewId}/fields`,
        {
          ontologyAttributeId,
          columnMetadataId,
          viewSourceId,
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
          timeout: 300000, // 5分钟超时
        }
      );

      if (response.data.code !== 200) {
        throw new Error(response.data.message || '映射视图添加字段映射失败');
      }
      return {
        message: response.data.message,
        code: response.data.code,
        data: response.data.data,
      };
    } catch (error) {
      console.error('❌ 映射视图添加字段映射失败:', error);
      throw error;
    }
  }
  // 映射试图删除字段映射
  static async mappingsViewsDeleteField(
    edgeId: string
  ): Promise<DataMapApiResponse<number>> {
    try {
      const response = await apiClient
        .getInstance()
        .delete(`/mapping-views/fields/${edgeId}`, {
          headers: {
            'Content-Type': 'application/json',
          },
          timeout: 300000, // 5分钟超时
        });

      if (response.data.code !== 200) {
        throw new Error(response.data.message || '映射字段删除字段映射失败');
      }
      return {
        message: response.data.message,
        code: response.data.code,
        data: response.data.data,
      };
    } catch (error) {
      console.error('❌ 映射字段删除字段映射失败:', error);
      throw error;
    }
  }
  // 映射视图删除数据表关联join
  static async mappingsViewsDeleteTableJoin(
    nodeId: string
  ): Promise<DataMapApiResponse<number>> {
    try {
      const response = await apiClient
        .getInstance()
        .delete(`/mapping-views/joins/${nodeId}`, {
          headers: {
            'Content-Type': 'application/json',
          },
          timeout: 300000, // 5分钟超时
        });

      if (response.data.code !== 200) {
        throw new Error(response.data.message || '映射字段删除字段映射失败');
      }
      return {
        message: response.data.message,
        code: response.data.code,
        data: response.data.data,
      };
    } catch (error) {
      console.error('❌ 映射字段删除字段映射失败:', error);
      throw error;
    }
  }
  // 映射视图删除数据表
  static async mappingsViewsDeleteTable(
    viewId: string,
    sourceId: string
  ): Promise<DataMapApiResponse<number>> {
    try {
      const response = await apiClient
        .getInstance()
        .delete(`/mapping-views/${viewId}/sources/${sourceId}`, {
          headers: {
            'Content-Type': 'application/json',
          },
          timeout: 300000, // 5分钟超时
        });

      if (response.data.code !== 200) {
        throw new Error(response.data.message || '映射字段删除字段映射失败');
      }
      return {
        message: response.data.message,
        code: response.data.code,
        data: response.data.data,
      };
    } catch (error) {
      console.error('❌ 映射字段删除字段映射失败:', error);
      throw error;
    }
  }
  // 映射试图更新字段映射
  static async mappingsViewsPutField(
    edgeId: string
  ): Promise<DataMapApiResponse<number>> {
    try {
      const response = await apiClient
        .getInstance()
        .put(`/mapping-views/fields/${edgeId}`, undefined, {
          headers: {
            'Content-Type': 'application/json',
          },
          timeout: 300000, // 5分钟超时
        });

      if (response.data.code !== 200) {
        throw new Error(response.data.message || '映射字段删除字段映射失败');
      }
      return {
        message: response.data.message,
        code: response.data.code,
        data: response.data.data,
      };
    } catch (error) {
      console.error('❌ 映射字段删除字段映射失败:', error);
      throw error;
    }
  }

  // 映射视图更新数据表关联join
  static async mappingsViewsPutTable(
    nodeId: string
  ): Promise<DataMapApiResponse<number>> {
    try {
      const response = await apiClient
        .getInstance()
        .put(`/mapping-views/joins/${nodeId}`, undefined, {
          headers: {
            'Content-Type': 'application/json',
          },
          timeout: 300000, // 5分钟超时
        });

      if (response.data.code !== 200) {
        throw new Error(response.data.message || '映射字段删除字段映射失败');
      }
      return {
        message: response.data.message,
        code: response.data.code,
        data: response.data.data,
      };
    } catch (error) {
      console.error('❌ 映射字段删除字段映射失败:', error);
      throw error;
    }
  }
  // 获取对象映射视图详情
  static async getObjectMappingDetail(
    ontologyObjectId: string
  ): Promise<DataMapApiResponse<any>> {
    try {
      const response = await apiClient
        .getInstance()
        .get(`/mapping-views/by-object/${ontologyObjectId}`, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          timeout: 300000, // 5分钟超时
        });
      if (response.data.code !== 200) {
        throw new Error(response.data.message || '获取对象映射视图详情失败');
      }

      return {
        message: response.data.message,
        code: response.data.code,
        data: response.data.data,
      };
    } catch (error) {
      console.error('❌ 获取对象映射视图详情失败:', error);
      throw error;
    }
  }
  // 本体可配置映射对象列表
  static async getSettingsMappingObjectsList(
    ontologyObjectId: string
  ): Promise<DataMapApiResponse<any>> {
    try {
      const response = await apiClient
        .getInstance()
        .get(`/ontologies/${ontologyObjectId}/mappable-objects`, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          timeout: 300000, // 5分钟超时
        });
      if (response.data.code !== 200) {
        throw new Error(
          response.data.message || '获取本体可配置映射对象列表失败'
        );
      }
      return {
        message: response.data.message,
        code: response.data.code,
        data: response.data.data,
      };
    } catch (error) {
      console.error('❌ 获取本体可配置映射对象列表失败:', error);
      throw error;
    }
  }
  // 根据本体节点查询数据映射使用的结构对象
  static async getStructureObjectByNodeId(
    ontologyId: string,
    objectNodeId: string
  ): Promise<DataMapApiResponse<OntologyStructureObject>> {
    try {
      const response = await apiClient
        .getInstance()
        .get(`/ontologies/${ontologyId}/object/${objectNodeId}/structure`, {
          headers: {
            'Content-Type': 'application/json',
          },
          timeout: 300000,
        });
      if (response.data.code !== 200) {
        throw new Error(response.data.message || '获取结构对象失败');
      }
      return {
        message: response.data.message,
        code: response.data.code,
        data: response.data.data,
      };
    } catch (error) {
      console.error('❌ 获取结构对象失败:', error);
      throw error;
    }
  }
  // 映射视图添加数据表关联join
  static async mapViewsAddTableJoins(
    mapViewsId: string,
    requestParams: mapViewsAddTableJoinsParmas
  ): Promise<DataMapApiResponse<any>> {
    try {
      const response = await apiClient
        .getInstance()
        .post(`/mapping-views/${mapViewsId}/joins`, requestParams, {
          headers: {
            'Content-Type': 'application/json',
          },
          timeout: 300000, // 5分钟超时
        });
      if (response.data.code !== 200) {
        throw new Error(response.data.message || '映射视图添加数据表关联join失败');
      }

      return {
        message: response.data.message,
        code: response.data.code,
        data: response.data.data,
      };
    } catch (error) {
      console.error('❌ 映射视图添加数据表关联join失败:', error);
      throw error;
    }
  }
  // 映射视图更新数据表关联join
  static async mapViewsUpdateTableJoins(
    mapViewsId: string,
    requestParams: mapViewsAddTableJoinsParmas
  ): Promise<DataMapApiResponse<any>> {
    try {
      const response = await apiClient
        .getInstance()
        .put(`/mapping-views/joins/${mapViewsId}`, requestParams, {
          headers: {
            'Content-Type': 'application/json',
          },
          timeout: 300000, // 5分钟超时
        });
      if (response.data.code !== 200) {
        throw new Error(response.data.message || '映射视图更新数据表关联join失败');
      }

      return {
        message: response.data.message,
        code: response.data.code,
        data: response.data.data,
      };
    } catch (error) {
      console.error('❌ 映射视图更新数据表关联join失败:', error);
      throw error;
    }
  }
  // 删除数据源已添加的数据表
  static async dataSourcesDeleteTable(
    viewId: string,
    sourceId: string
  ): Promise<DataMapApiResponse<number>> {
    try {
      const response = await apiClient
        .getInstance()
        .delete(`/datasources/${viewId}/metadata/${sourceId}`, {
          headers: {
            'Content-Type': 'application/json',
          },
          timeout: 300000, // 5分钟超时
        });

      if (response.data.code !== 200) {
        throw new Error(response.data.message || '删除数据源数据表失败');
      }
      return {
        message: response.data.message,
        code: response.data.code,
        data: response.data.data,
      };
    } catch (error) {
      console.error('❌ 删除数据源数据表失败:', error);
      throw error;
    }
  }
}
